'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import { blogExcerpt, type BlogCardData } from '../../utils/blog-card-data';

import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { BiCalendar } from 'react-icons/bi';
import { LuArrowUpRight } from 'react-icons/lu';

export type BlogCardProps = {
  className?: {
    main?: string;
    link?: string;
    image?: string;
    title?: string;
    date?: string;
    contentContainer?: string;
    content?: string;
  };
  blog: BlogCardData;
  firstBlog?: boolean;
  headingTag?: 'h2' | 'h3';
};

export function BlogCard({ blog, className, firstBlog = false, headingTag: Title = 'h3', ...props }: BlogCardProps) {
  const locale = useLocaleStore(state => state.currentLocale);

  return (
    <div
      className={cn(
        'flex w-full flex-col',
        firstBlog
          ? 'col-span-1 gap-4 sm:col-span-2 sm:gap-6 md:mb-2 md:flex-row md:items-center md:justify-between lg:col-span-3 lg:mb-4 lg:gap-8'
          : 'col-span-1 items-start justify-start gap-4',
      )}
    >
      <Link
        prefetch={false}
        href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
        aria-label={blog.name}
        className={cn(
          'relative h-[205px] w-full max-w-[913px] overflow-hidden rounded-[16px]',
          firstBlog
            ? 'sm:h-[300px] lg:h-[420px] xl:h-[520px]'
            : 'md:h-[187px] lg:h-[220px] xl:h-[232px]',
        )}
      >
        <Image
          src={blog?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          fill
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          sizes="(max-width: 640px) 90vw, (max-width: 768px) 50vw, 30vw"
          className="absolute inset-0 z-0 h-full w-full"
        />
      </Link>
      <div className="flex w-full flex-col items-start justify-start gap-4">
        <Title
          data-ea-visual-tag="h4"
          className={cn(
            'w-full font-bold text-foreground',
            'line-clamp-2',
            firstBlog
              ? 'text-base sm:text-xl md:text-2xl xl:text-3xl'
              : 'h-[52px] text-base sm:h-[58px] sm:text-lg',
          )}
        >
          {blog.name}
        </Title>
        <p className="line-clamp-2 max-h-10 overflow-hidden text-sm leading-5 text-foreground">
          {blog.excerpt ?? blogExcerpt(blog.content)}
        </p>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center justify-start gap-2 text-xs text-[#969696] sm:text-sm">
            <BiCalendar size={20} />
            {new Date(blog.created_at).toLocaleDateString(locale, {
              timeZone: 'Europe/Bratislava',
            })}
          </div>
          <Button asChild className="w-fit">
            <Link prefetch={false} href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
              aria-label={`Prečítať článok: ${blog.name}`}>
              Prečítať článok <LuArrowUpRight size={20} />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
